// src/App.js
import React, { Suspense, lazy, useState } from 'react';
import './App.css';
import Home from './components/Home';
import Header from './components/Header';
import Footer from './components/Footer';
import LazySection from './components/LazySection';
import ScrollArrow from './components/ScrollArrow';
import SplashScreen from './components/SplashScreen';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import ScrollProgress from './components/ScrollProgress';
import CustomCursor from './components/CustomCursor';
import PageTransition from './components/PageTransition';
import { logCustomEvent } from './firebase';
import { BackgroundAudioProvider } from './components/BackgroundAudio';
import { isMobileDevice } from './utils/isMobileDevice';

const About = lazy(() => import('./components/About'));
const Experience = lazy(() => import('./components/Experience'));
const PortfolioPreview = lazy(() => import('./components/PortfolioPreview'));
const Skills = lazy(() => import('./components/Skills'));
const Services = lazy(() => import('./components/Services'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const Projects = lazy(() => import('./components/Projects'));
const Contact = lazy(() => import('./components/Contact'));
const ProjectDetails = lazy(() => import('./components/ProjectDetails'));
const WebAppDetails = lazy(() => import('./components/WebAppDetails'));
const AiAppDetails = lazy(() => import('./components/AiAppDetails'));

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const location = useLocation();
  const isMobile = isMobileDevice();

  React.useEffect(() => {
    logCustomEvent('page_view', {
      page_path: location.pathname,
      page_search: location.search,
      page_title: document.title || 'Junaid Nasir Portfolio',
    });
  }, [location]);

  return (
    <>
      {!isMobile && <ScrollProgress />}
      {!isMobile && <CustomCursor />}
      <ScrollArrow />
      <div className="App">
        <ScrollToTop />
        <Header />
        <PageTransition location={location.pathname}>
          <Suspense fallback={null}>
            <Routes location={location}>
              <Route
                path="/"
                element={
                  <>
                    <Home />
                    <LazySection>
                      <About />
                    </LazySection>
                    <LazySection>
                      <Experience />
                    </LazySection>
                    <LazySection>
                      <PortfolioPreview />
                    </LazySection>
                    <LazySection>
                      <Skills />
                    </LazySection>
                    <LazySection>
                      <Services />
                    </LazySection>
                    <LazySection>
                      <Testimonials />
                    </LazySection>
                  </>
                }
              />
              <Route path="/Projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/web-apps/:id" element={<WebAppDetails />} />
              <Route path="/ai-apps/:id" element={<AiAppDetails />} />
              <Route path="/Contact" element={<Contact />} />
            </Routes>
          </Suspense>
        </PageTransition>
        <Footer />
      </div>
    </>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <>
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <div className={showSplash ? 'app-shell app-shell--hidden' : 'app-shell'}>
        <Router>
          <BackgroundAudioProvider>
            <AppContent />
          </BackgroundAudioProvider>
        </Router>
      </div>
    </>
  );
}

export default App;
